/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AEvent
 */

import { IPlanObject } from 'com/didichuxing/IPlanObject';
import { IObjectMap } from 'com/didichuxing/IObjectMap';
import { IContext } from 'com/didichuxing/context/IContext';
import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { IContextContainer } from 'com/didichuxing/context/IContextContainer';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventData } from 'com/didichuxing/tracker/event/ITrackedEventData';
import { IAttrs } from 'com/didichuxing/tracker/IAttrs';
import { EventStatus } from 'com/didichuxing/tracker/plugin/events/EventStatus';
import GlobalUtil from 'com/didichuxing/tracker/plugin/utils/GlobalUtil';

export default abstract class AEvent implements ITrackedEvent, IContextContainer {

    /**
     * 事件生成的额外信息
     */
    private readonly info?: IAttrs;

    /**
     * 上报的上下文环境
     */
    private readonly context: IContext;

    /**
     * 事件的额外参数。
     */
    private readonly attrs: IAttrs;

    /**
     * @constructor
     * @param {IAttrs} attrs
     * @param {IContext} context
     * @param {IAttrs} info
     */
    constructor(attrs: IAttrs, context: IContext, info?: IAttrs) {
        this.attrs = attrs;
        this.context = context;
        this.info = info;
    }

    /**
     * 获取版本号
     */
    private getVersion() {
        return {
            trackerVersion: this.getContext().version
        };
    }

    /**
     * @abstract
     * @returns {string}
     */
    public abstract getType(): string;

    /**
     * @override
     * @inheritDoc
     * @returns {ITrackedEventData}
     */
    public getData(): ITrackedEventData {
        const context: ITrackerContext = this.getContext() as ITrackerContext;
        let appKey = context.appKey || context.productName;
        /**
         * 当配置appkey可修改的时候，将event生成的信息中的appkey进行填充
         */
        if ( context && context.modifyAppKey && this.info && this.info.appKey) {
            appKey = this.info.appKey;
        }
        return {
            eventId: this.getType(),
            timestamp: new Date().getTime(),
            viewportId: context.viewportId,
            appName: context.appName,
            appVersion: context.appVersion,
            omgId: context.omgId,
            url: context.url || location.href,
            latitude: context.latitude,
            longitude: context.longitude,
            userAgent: context.userAgent,
            osType: context.osType,
            osVersion: context.osVersion,
            appKey: appKey,
            userName: context.userName,
            userId: context.userId,
            cityId: context.cityId,
            telephone: context.telephone,
            bizId: context.bizId,
            locale: context.locale,
            countyId: context.countyId,
            countryCallingCode: context.countryCallingCode,
            utcOffset: context.utcOffset,
            attrs: this.getAttrs(),
            from: this.getFrom()
        };
    }

    /**
     * 将事件数据属性映射成上报的字段。
     * @returns {IObjectMap<string>}
     */
    protected getDataKeyMap(): IObjectMap<string> {
        return {
            eventId: 'e',
            timestamp: 'ts',
            viewportId: 'uwid',
            version: 'jv',
            appName: 'an',
            appVersion: 'av',
            omgId: 'oid',
            url: 'v',
            latitude: 'lat',
            longitude: 'lng',
            userAgent: 'ua',
            osType: 'ot',
            osVersion: 'ov',
            appKey: 'ak',
            userName: 'un',
            userId: 'uid',
            cityId: 'cityid',
            telephone: 'tel',
            bizId: 'bizId',
            locale: 'le',
            countyId: 'coi',
            countryCallingCode: 'dcc',
            utcOffset: 'uo',
            from: 'fr'
        };
    }

    /**
     * 将attrs属性映射成上报的字段。
     * @returns {IObjectMap<string>}
     */
    protected getAttrsKeyMap(): IObjectMap<string> {
        return {
            _label: 'l',
            trackerVersion: 'jv'
        };
    }

    /**
     * 把event data（去除attrs）序列化成json字符串。
     * @returns {string}
     */
    public serialize2JsonStrWithoutAttrs(): string {
        try {
            const eventData: ITrackedEventData = this.getData();
            const obj: IPlanObject = {};
            const dataKeyMap: IObjectMap<string> = this.getDataKeyMap();
            for (const prop in eventData) {
                if (prop !== 'attrs' && !/Null|Undefined/.test(Object.prototype.toString.call(eventData[prop]))) {
                    obj[dataKeyMap[prop] || prop] = eventData[prop];
                }
            }
            return JSON.stringify(obj);
        } catch (e) {
            GlobalUtil.dispatchOmgLog('serialize2JsonStrWithoutAttrs Error');
            return '';
        }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {string}
     */
    public serialize(): string {
        try {
            const eventData: ITrackedEventData = this.getData();
            // 先用Object内置类的keys方法获取要排序对象的属性名，再利用Array原型上的sort方法对获取的属性名进行排序，newkey是一个数组
            const newkeys = Object.keys(eventData).sort();
            const kvs: string[] = [];
            const dataKeyMap: IObjectMap<string> = this.getDataKeyMap();
            // 要保证顺序 所以attrs也放进了循环
            newkeys.forEach((prop) => {
                if (prop !== 'attrs' && !/Null|Undefined/.test(Object.prototype.toString.call(eventData[prop]))) {
                    kvs.push(`${ dataKeyMap[prop] || prop }=${ encodeURIComponent(eventData[prop]) }`);
                } else if (prop === 'attrs') {
                    const attrsJsonStr: string = this.serializeAttrs2JsonStr(eventData.attrs || {});
                    if (attrsJsonStr !== '{}') {
                        kvs.push(`attrs=${ encodeURIComponent(attrsJsonStr) }`);
                    }
                }
            });
            return kvs.join('&');
        } catch (e) {
            GlobalUtil.dispatchOmgLog('serialize Error');
            return '';
        }
    }

    /**
     * 格式化attrs
     * @param {IAttrs} attrs
     * @returns {string}
     */
    public serializeAttrs2JsonStr(attrs: IAttrs): string {
        try {
            const serializedAttrs: IPlanObject = {};
            const attrsKeyMap: IObjectMap<string> = this.getAttrsKeyMap();
            for (const prop in attrs) {
                if (attrs.hasOwnProperty(prop)) {
                    const key: string = attrsKeyMap[prop] || prop;
                    if (/Object|Array/.test(Object.prototype.toString.call(attrs[prop]))) {
                        serializedAttrs[key] = JSON.stringify(attrs[prop]);
                    } else if (/String/.test(Object.prototype.toString.call(attrs[prop])) && attrs[prop]) {
                        serializedAttrs[key] = attrs[prop];
                    } else if (/Number/.test(Object.prototype.toString.call(attrs[prop]))) {
                        serializedAttrs[key] = attrs[prop];
                    } else if (/Boolean/.test(Object.prototype.toString.call(attrs[prop]))) {
                        serializedAttrs[key] = attrs[prop] | 0;
                    }
                }
            }
            return JSON.stringify(serializedAttrs);
        } catch (e) {
            GlobalUtil.dispatchOmgLog('serializeAttrs2JsonStr Error');
            return '';
        }

    }

    /**
     * @override
     * @inheritDoc
     * @returns {IContext}
     */
    public getContext(): IContext {
        return this.context;
    }

    /**
     * 该事件的额外参数。
     * @returns {IAttrs}
     */
    protected getAttrs(): IAttrs {
        const thisAttrs: any = {};
        const context = this.getContext() as ITrackerContext;
        if (context.openId) {
            thisAttrs['_OMGPUID'] = context.openId;
        }

        return Object.assign(thisAttrs, context.attrs || {}, this.attrs, this.getVersion());
    }

    /**
     * 获取框架来源
     * 由业务方指定
     */
    protected getFrom(): string {
        const context: ITrackerContext = this.getContext() as ITrackerContext;
        if (GlobalUtil.isEmptyString(context.from)) {
            return `h5-${ context.from }`;
        } else {
            return 'h5';
        }
    }

    /**
     * 事件的状态。
     * {@link com/didichuxing/tracker/h5/plugins/events/EventStatus}
     * @returns {EventStatus}
     */
    public abstract get status(): EventStatus;
}
